import { useCallback, useEffect, useState } from 'react';
import { Field } from '@/shared/ui/Field';
import { InputMonetario } from '@/shared/ui/InputMonetario';
import { Modal } from '@/shared/ui/Modal';
import {
  pdvApi,
  type CaixaResumo,
  type ResumoCaixa,
} from '../api/pdvApi';

/** O modal daqui só faz os dois manuais; recebimento vem da tela de fiado. */
type TipoMovimentoManual = 'Sangria' | 'Suprimento';
import { formatarDataHora, formatarDinheiro, textoDoErro } from '../formato';

type Props = {
  caixa: CaixaResumo;
  onFechado: () => void;
  onVoltar: () => void;
};

/**
 * Fechamento com conferência da gaveta.
 *
 * A tela mostra duas contas separadas de propósito: o dinheiro que deve
 * estar na gaveta (abertura + o que entrou em dinheiro) e o total que vai
 * para o financeiro (todas as formas). Eles divergem sempre que houver
 * cartão ou Pix, e isso é correto.
 */
export function PdvFechamentoCaixa({ caixa, onFechado, onVoltar }: Props) {
  const [resumo, setResumo] = useState<ResumoCaixa | null>(null);
  const [pin, setPin] = useState('');
  const [valorInformado, setValorInformado] = useState(0);
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [movimentando, setMovimentando] = useState<TipoMovimentoManual | null>(null);

  const recarregar = useCallback(async () => {
    try {
      setResumo(await pdvApi.resumoCaixa(caixa.id));
    } catch (e) {
      setErro(textoDoErro(e));
    }
  }, [caixa.id]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const diferenca = resumo ? Math.round((valorInformado - resumo.esperadoGaveta) * 100) / 100 : 0;
  const bate = Math.abs(diferenca) < 0.01;

  async function fechar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await pdvApi.fecharCaixa(caixa.id, { pin, valorInformado, justificativa });
      onFechado();
    } catch (err) {
      setErro(textoDoErro(err));
      setPin('');
    } finally {
      setSalvando(false);
    }
  }

  if (!resumo) {
    return (
      <div className="pdv-centro pdv-centro--vazio">
        {erro ?? 'Carregando o resumo do caixa…'}
      </div>
    );
  }

  return (
    <div className="pdv-centro">
      <form className="pdv-cartao pdv-cartao--largo" onSubmit={fechar}>
        <h2 className="pdv-cartao__titulo">Fechar caixa</h2>
        <p className="pdv-cartao__ajuda">
          Aberto em {formatarDataHora(caixa.abertoEm)} por {caixa.colaboradorNome ?? '—'}.
        </p>

        <div className="pdv-fechamento">
          <section className="pdv-fechamento__bloco">
            <h3 className="pdv-fechamento__titulo">Dinheiro na gaveta</h3>
            <dl className="pdv-fechamento__linhas">
              <div>
                <dt>Abertura</dt>
                <dd>{formatarDinheiro(resumo.valorAbertura)}</dd>
              </div>
              <div>
                <dt>Vendas em dinheiro</dt>
                <dd>{formatarDinheiro(resumo.totalDinheiro)}</dd>
              </div>
              {resumo.suprimentos > 0 && (
                <div>
                  <dt>Suprimentos</dt>
                  <dd>+ {formatarDinheiro(resumo.suprimentos)}</dd>
                </div>
              )}
              {resumo.recebimentos > 0 && (
                <div>
                  <dt>Fiado recebido</dt>
                  <dd>+ {formatarDinheiro(resumo.recebimentos)}</dd>
                </div>
              )}
              {resumo.sangrias > 0 && (
                <div>
                  <dt>Sangrias</dt>
                  <dd>− {formatarDinheiro(resumo.sangrias)}</dd>
                </div>
              )}
              <div className="pdv-fechamento__total">
                <dt>Deve estar na gaveta</dt>
                <dd>{formatarDinheiro(resumo.esperadoGaveta)}</dd>
              </div>
            </dl>

            <div className="pdv-fechamento__acoes">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setMovimentando('Sangria')}
              >
                Retirar (sangria)
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setMovimentando('Suprimento')}
              >
                Repor troco
              </button>
            </div>

            {resumo.movimentos.length > 0 && (
              <ul className="pdv-movimentos">
                {resumo.movimentos.map((m) => (
                  <li key={m.id} className="pdv-movimentos__item">
                    <span className={`pdv-movimentos__tipo pdv-movimentos__tipo--${m.tipo.toLowerCase()}`}>
                      {m.tipo === 'Sangria' ? '−' : '+'} {formatarDinheiro(m.valor)}
                    </span>
                    <span className="pdv-movimentos__motivo">{m.motivo}</span>
                    <span className="pdv-movimentos__hora">{formatarDataHora(m.criadoEm)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="pdv-fechamento__bloco">
            <h3 className="pdv-fechamento__titulo">Vai para o financeiro</h3>
            <dl className="pdv-fechamento__linhas">
              {resumo.totaisPorForma.map((f) => (
                <div key={f.formaPagamento}>
                  <dt>{f.formaPagamento}</dt>
                  <dd>{formatarDinheiro(f.valor)}</dd>
                </div>
              ))}
              {resumo.totaisPorForma.length === 0 && (
                <div>
                  <dt>Nenhuma venda</dt>
                  <dd>—</dd>
                </div>
              )}
              <div className="pdv-fechamento__total">
                <dt>Total ({resumo.quantidadeVendas} venda(s))</dt>
                <dd>{formatarDinheiro(resumo.totalConsolidado)}</dd>
              </div>
            </dl>
            <p className="pdv-fechamento__nota">
              Cartão e Pix não passam pela gaveta — por isso os dois valores acima costumam ser
              diferentes.
            </p>
            {resumo.recebimentos > 0 && (
              <p className="pdv-fechamento__nota">
                {formatarDinheiro(resumo.recebimentos)} de fiado recebido hoje estão na gaveta, mas
                fora deste total: essa receita já foi registrada quando a venda foi feita.
              </p>
            )}
            {resumo.totalFiado > 0 && (
              <p className="pdv-fechamento__nota">
                {formatarDinheiro(resumo.totalFiado)} no fiado ficam fora deste total: cada venda já
                tem seu próprio lançamento a receber, com o cliente e o vencimento.
              </p>
            )}
          </section>
        </div>

        <Field
          rotulo="Quanto tem na gaveta agora"
          dica="Conte o dinheiro e digite o valor encontrado."
        >
          {(id) => (
            <InputMonetario
              id={id}
              className="input pdv-num pdv-num--valor"
              valor={valorInformado}
              onChange={setValorInformado}
            />
          )}
        </Field>

        {!bate && (
          <div className={`pdv-diferenca ${diferenca < 0 ? 'pdv-diferenca--falta' : 'pdv-diferenca--sobra'}`}>
            <span>{diferenca < 0 ? 'Falta' : 'Sobra'}</span>
            <strong>{formatarDinheiro(Math.abs(diferenca))}</strong>
          </div>
        )}

        {!bate && (
          <Field
            rotulo="O que aconteceu"
            obrigatorio
            dica="Obrigatório quando a gaveta não bate. É o que transforma a diferença em informação."
          >
            {(id) => (
              <textarea
                id={id}
                className="input"
                rows={2}
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                placeholder="Ex.: peguei R$40 para o almoço da equipe."
                required
              />
            )}
          </Field>
        )}

        <Field rotulo="Seu PIN" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input pdv-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••"
              required
            />
          )}
        </Field>

        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        <div className="pdv-form-modal__acoes">
          <button type="button" className="btn btn--ghost" onClick={onVoltar}>
            Voltar
          </button>
          <button type="submit" className="btn btn--primary" disabled={salvando}>
            {salvando ? 'Fechando…' : 'Fechar caixa'}
          </button>
        </div>
      </form>

      {movimentando && (
        <ModalMovimento
          tipo={movimentando}
          esperadoGaveta={resumo.esperadoGaveta}
          caixaId={caixa.id}
          onFechar={() => setMovimentando(null)}
          onSalvo={() => {
            setMovimentando(null);
            void recarregar();
          }}
        />
      )}
    </div>
  );
}

/**
 * Sangria ou suprimento. Motivo é obrigatório: é o que transforma a
 * diferença da gaveta em informação em vez de mistério na conferência.
 */
function ModalMovimento({
  tipo,
  caixaId,
  esperadoGaveta,
  onSalvo,
  onFechar,
}: {
  tipo: TipoMovimentoManual;
  caixaId: string;
  esperadoGaveta: number;
  onSalvo: () => void;
  onFechar: () => void;
}) {
  const [valor, setValor] = useState(0);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const sangria = tipo === 'Sangria';

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await pdvApi.registrarMovimento(caixaId, { tipo, valor, motivo });
      onSalvo();
    } catch (err) {
      setErro(textoDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      titulo={sangria ? 'Retirar da gaveta' : 'Repor troco na gaveta'}
      onFechar={onFechar}
    >
      <form className="pdv-form-modal" onSubmit={salvar}>
        <p className="pdv-cartao__ajuda">
          {sangria
            ? `Há ${formatarDinheiro(esperadoGaveta)} na gaveta agora.`
            : 'Dinheiro que entra sem ser venda — buscar troco no cofre, por exemplo.'}
        </p>

        <Field rotulo="Valor" obrigatorio>
          {(id) => (
            <InputMonetario
              id={id}
              className="input pdv-num pdv-num--valor"
              valor={valor}
              onChange={setValor}
            />
          )}
        </Field>

        <Field rotulo="Motivo" obrigatorio dica="Sem ele, a conferência do fim do dia vira adivinhação.">
          {(id) => (
            <input
              id={id}
              className="input"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={sangria ? 'Ex.: depósito no banco.' : 'Ex.: troco trazido do cofre.'}
              autoFocus
              required
            />
          )}
        </Field>

        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        <div className="pdv-form-modal__acoes">
          <button type="button" className="btn btn--ghost" onClick={onFechar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={salvando || valor <= 0 || motivo.trim().length < 3}
          >
            {salvando ? 'Registrando…' : sangria ? 'Retirar' : 'Repor'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
